import React from 'react';
import { Box, CircularProgress, Grid, List, ListItemButton, ListItemText, Paper, TextField, } from '@mui/material';
import { useEffect, useState } from 'react';

const AddressStreetAutocompleteField: React.FC<AddressStreetAutocompleteFieldProps> = ({ formikProps, config, md = 7.8, xs = 5.8, label = 'Street Address', placeholder, countryOptions, }) => {
  const { values, errors, touched, handleBlur, setFieldValue, setFieldTouched, submitCount } = formikProps;

  const streetName = config.streetField;
  const countryName = config.countryField;

  const streetValue: string = values?.[streetName] ?? '';
  const countryValue: any = values?.[countryName];

  const streetTouched = touched?.[streetName];
  const streetError = errors?.[streetName];
  const hasSubmitted = (submitCount ?? 0) > 0;
  const showError = (streetTouched || hasSubmitted) && streetError;

  // Map numeric/id value -> option -> label (e.g. 205 -> "SOMALIA")
  const selectedCountryOption = countryOptions?.find((opt) => opt.value === countryValue);
  const countryFilterText = selectedCountryOption?.label;

  const { query, setQuery, suggestions, loading, error, clearSuggestions } = useAddressAutocomplete(countryFilterText);

  // Keep hook query in sync with Formik value when user types
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setFieldValue(streetName, newValue);
    setQuery(newValue);
  };

  const handleBlurWrapper = (event: React.FocusEvent<HTMLInputElement>) => {
    setFieldTouched(streetName, true);
    if (typeof handleBlur === 'function') {
      handleBlur(event);
    }
  };

  const handleSelectSuggestion = (s: AddressSuggestion) => {
    // Use strict street if we have it, otherwise fall back to primaryText
    const streetToUse = s.street || s.primaryText || '';
    setFieldValue(streetName, streetToUse);
    setFieldTouched(streetName, true);
    if (config.cityField && s.city) {
      setFieldValue(config.cityField, s.city);
    }
    if (config.stateField && s.state) {
      setFieldValue(config.stateField, s.state);
    }
    if (config.postcodeField && s.postcode) {
      setFieldValue(config.postcodeField, s.postcode);
    }

    // IMPORTANT: do NOT overwrite the country field in this demo.
    // The form stores a numeric ID; OSM gives ISO2/labels, they will not match.
    // When you later wire a real provider via backend, you can implement
    // a proper mapping there.
    clearSuggestions();
  };

  const helperText = showError
    ? streetError
    : error
      ? error
      : '';

  return (
    <Grid item display="flex" md={md} xs={xs}>
      <Grid item md={12} xs={12} display="flex" alignItems="center" width="100%">
        <Box position="relative" width="100%">
          <TextField fullWidth label={label} name={streetName} value={streetValue} onChange={handleChange} onBlur={handleBlurWrapper} placeholder={placeholder} error={Boolean(showError)} helperText={helperText} margin="normal" variant="outlined" InputProps={{ endAdornment: loading ? (<CircularProgress size={18} />) : undefined, }} />
          {Boolean(suggestions.length) && (
            <Paper elevation={3} sx={{ position: 'absolute', zIndex: 20, left: 0, right: 0, top: '100%', maxHeight: 300, overflowY: 'auto', }}>
              <List dense>
                {suggestions.map((s) => (
                  <ListItemButton key={s.id} onClick={() => handleSelectSuggestion(s)}>
                    <ListItemText primary={s.primaryText || s.street} secondary={s.secondaryText} />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org/search';

// NOTE: This is fine for a light dev/demo.
// For any serious use, you should proxy this via your backend and follow
// Nominatim’s usage policy strictly.
async function fetchAddressSuggestions(query: string, countryFilterText?: string): Promise<AddressSuggestion[]> {
  const params: Record<string, string> = {
    // If we know the country label (e.g. "SOMALIA"),
    // bias the search by appending it to the query.
    q: countryFilterText ? `${query}, ${countryFilterText}` : query,
    format: 'json',
    addressdetails: '1',
    limit: '5',
  };
  const url = `${NOMINATIM_BASE_URL}?${new URLSearchParams(params).toString()}`;
  const res = await fetch(url, { headers: { Accept: 'application/json', }, });
  if (!res.ok) {
    throw new Error(`Nominatim error: ${res.status}`);
  }
  const data = (await res.json()) as any[];
  return data.map((item, idx) => {
    const addr = item.address || {};
    const road =
      addr.road ||
      addr.pedestrian ||
      addr.footway ||
      addr.cycleway ||
      addr.path ||
      '';
    const houseNumber = addr.house_number || '';
    // STRICT: street is only road + house number
    const street = [road, houseNumber].filter(Boolean).join(' ');
    const city =
      addr.city ||
      addr.town ||
      addr.village ||
      addr.hamlet ||
      addr.municipality ||
      '';
    const state = addr.state || '';
    const postcode = addr.postcode || '';
    const countryCodeRes = addr.country_code
      ? String(addr.country_code).toUpperCase()
      : undefined;
    const displayName: string = item.display_name ?? '';
    const firstPart = displayName ? displayName.split(',')[0].trim() : '';
    const primaryText = street || firstPart || displayName;
    const secondaryText = displayName || undefined;
    return {
      id: item.place_id?.toString() ?? `${idx}-${item.lat}-${item.lon}`,
      primaryText,
      secondaryText,
      // keep street as ONLY road + house number
      street,
      city: city || undefined,
      state: state || undefined,
      postcode: postcode || undefined,
      countryCode: countryCodeRes,
      raw: item,
    } as AddressSuggestion;
  });
}

export function useAddressAutocomplete(countryFilterText?: string) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounce the query
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(handle);
  }, [query]);
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 3) {
      setSuggestions([]);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchAddressSuggestions(debouncedQuery, countryFilterText);
        if (!cancelled) {
          setSuggestions(result);
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setError('Failed to search address');
          setSuggestions([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, countryFilterText]);
  const clearSuggestions = () => {
    setSuggestions([]);
  };
  return { query, setQuery, suggestions, loading, error, clearSuggestions, };
}

export default AddressStreetAutocompleteField;

export interface AddressSuggestion {
  id: string;
  primaryText: string;      // What you show as main line in the list
  secondaryText?: string;   // Optional smaller text
  street: string;           // Only street + house number, if available
  city?: string;
  state?: string;
  postcode?: string;
  countryCode?: string;     // ISO2 if you have it (from OSM)
  raw?: any;                // Provider-specific payload
}

export interface AddressFieldConfig {
  streetField: string;   // required, e.g. "Address_Street"
  countryField: string;  // required, e.g. "Address_Country"

  cityField?: string;    // optional, e.g. "Address_City"
  stateField?: string;   // optional, e.g. "Address_State"
  postcodeField?: string; // optional, e.g. "Address_Postcode"

  // later you can add:
  // prefix?: string;
  // customFieldMap?: { [logicalName: string]: string };
}

interface AddressStreetAutocompleteFieldProps {
  formikProps: any;
  config: AddressFieldConfig;

  // layout + label
  md?: number;
  xs?: number;
  label?: string;
  placeholder?: string;

  // needed so we can translate the numeric country id to a label
  countryOptions?: { value: any; label: string }[];
}
